import { useEffect, useState } from 'react';
import { fetchAsinMaterials, saveImageToTemporary } from '../../../../features/materials/api';
import { getProduct } from '../../../../features/products/api';
import { resolveAiSourceUrl, resolveMaterialUrls } from '../../../../features/materials/materialUrl';
import { calculateAspectRatio } from '../../../materials/shared/materialUtils';
import { resolveErrorMessage } from '../../../../utils/getErrorMessage';

// 商品画像取得の自動リトライ設定。初回 + RETRY_LIMIT 回 = 計 (RETRY_LIMIT + 1) 回試行。
// ASIN 経路・マニュアル商品経路で共用。
const PRODUCT_IMAGE_RETRY_LIMIT = 2;
const PRODUCT_IMAGE_RETRY_BASE_MS = 800;

/**
 * 一時的な失敗 (リトライで直る可能性がある) かを判定する。
 * - ネットワーク障害 (response なし) / 5xx / バックエンドの IMAGE_FETCH_FAILED → リトライ対象
 * - NO_IMAGES_IN_DATABASE や 4xx (画像が本当に無い・入力不正) → リトライ無駄なので対象外
 */
function isRetriableFetchError(error: any): boolean {
  const status = error?.response?.status;
  const errorCode = error?.response?.data?.error_code;
  if (errorCode === 'NO_IMAGES_IN_DATABASE') return false;
  if (!error?.response) return true; // ネットワーク障害・タイムアウト
  if (errorCode === 'IMAGE_FETCH_FAILED') return true;
  if (typeof status === 'number' && status >= 500) return true;
  return false;
}

interface UseProductMaterialsParams {
  // setup フェーズでのみ自動補完を有効化する。
  // work フェーズでは in-flight 処理を cleanup で止め、意図しない素材復活を防ぐ。
  enabled: boolean;
  asin: string;
  manualProductId: string | null;
  fetchedAsins: Set<string>;
  setFetchedAsins: React.Dispatch<React.SetStateAction<Set<string>>>;
  fetchedProductIds: Set<string>;
  setFetchedProductIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  addAsset: (asset: any, opts?: { silent?: boolean }) => void;
  requestOpenPersonalFolder: () => void;
  toast: { show: (msg: string) => void; error: (msg: string) => void; success: (msg: string) => void };
  queryClient: any;
}

export function useProductMaterials(params: UseProductMaterialsParams) {
  const {
    enabled,
    asin,
    manualProductId,
    fetchedAsins,
    setFetchedAsins,
    fetchedProductIds,
    setFetchedProductIds,
    addAsset,
    requestOpenPersonalFolder,
    toast,
    queryClient,
  } = params;

  const [productImageFetchError, setProductImageFetchError] = useState<string | null>(null);
  const [productImageFetching, setProductImageFetching] = useState(false);

  // ASINが選択されたら商品画像を取得して選択中素材に追加
  useEffect(() => {
    if (!enabled) return;
    if (!asin) return;

    // 既に取得済みのASINはスキップ
    if (fetchedAsins.has(asin)) return;

    let cancelled = false;

    const fetchAndAddMaterials = async () => {
      try {
        setProductImageFetchError(null);
        setProductImageFetching(true);
        toast.show(`商品画像を取得中... (ASIN: ${asin})`);

        // 一時障害 (DB タイムアウト等) は自動リトライする。NO_IMAGES_IN_DATABASE 等の
        // リトライ無駄な失敗は isRetriableFetchError=false で即座に throw → 下の catch へ。
        let materials: Awaited<ReturnType<typeof fetchAsinMaterials>> | undefined;
        for (let attempt = 0; ; attempt++) {
          try {
            materials = await fetchAsinMaterials(asin);
            break;
          } catch (err) {
            if (cancelled) return;
            if (attempt >= PRODUCT_IMAGE_RETRY_LIMIT || !isRetriableFetchError(err)) throw err;
            toast.show(`商品画像の取得を再試行中... (${attempt + 1}/${PRODUCT_IMAGE_RETRY_LIMIT})`);
            await new Promise((resolve) => setTimeout(resolve, PRODUCT_IMAGE_RETRY_BASE_MS * (attempt + 1)));
            if (cancelled) return;
          }
        }

        if (cancelled) return;

        // 個人フォルダのキャッシュを更新
        await queryClient.refetchQueries({ queryKey: ['temporary-materials'] });

        if (!materials || materials.length === 0) {
          // 画像が0件の場合、fetchedAsinsに登録しない → リトライ可能
          toast.error('商品画像が見つかりませんでした。再取得ボタンで再試行してください。');
          setProductImageFetchError('商品画像が見つかりませんでした');
          return;
        }

        // すべての画像を選択中素材に追加
        materials.forEach((material) => {
          const urls = resolveMaterialUrls(material);

          // silent: true — 自動補完経路のため scenario の user_selected_asset_ids 保存
          // トリガを発火させない。setup 段階 (scope=STILL_IMAGE_SCOPE) なら markDirty は
          // 早期 return するが、念のため明示する。
          addAsset({
            id: material.id,
            thumbnailUrl: urls.thumbnail || '',
            sourceUrl: resolveAiSourceUrl(material, urls),
            type: 'temporary',
            name: material.name ?? null,
            storageBucket: material.storage_bucket || null,
            storagePath: material.storage_path || null,
            contentType: material.content_type || null,
            usage: 'reference',
            asin: material.asin || asin,
            hasProductLinks: true,
            width: material.width || null,
            height: material.height || null,
            aspectRatio: calculateAspectRatio(material.width, material.height),
          }, { silent: true });
        });

        // 取得済みASINとして記録（成功した場合のみ）
        setFetchedAsins((prev) => new Set(prev).add(asin));

        toast.success(`${materials.length}枚の画像を選択中素材に追加しました`);
        // 個人フォルダタブを開く
        requestOpenPersonalFolder();
      } catch (error: any) {
        if (cancelled) return;
        console.error('Failed to fetch ASIN materials:', error);

        const axiosErr = error as any;
        const errorCode = axiosErr?.response?.data?.error_code;
        const errorMessage = axiosErr?.response?.data?.error || resolveErrorMessage(error);

        if (errorCode === 'NO_IMAGES_IN_DATABASE') {
          const msg = `${errorMessage}`;
          toast.error(`${msg}\n\nAmazon Seller Centralの「商品管理」から商品情報を取得してください。`);
          setProductImageFetchError(msg);
        } else if (axiosErr.response?.status === 404) {
          const msg = 'エンドポイントが見つかりません (404)';
          toast.error(`商品画像の取得に失敗しました: ${msg}。バックエンドサーバーが起動しているか確認してください。`);
          setProductImageFetchError(`商品画像の取得に失敗しました: ${msg}`);
        } else {
          toast.error(`商品画像の取得に失敗しました: ${errorMessage}`);
          setProductImageFetchError(`商品画像の取得に失敗しました: ${errorMessage}`);
        }
      } finally {
        if (!cancelled) setProductImageFetching(false);
      }
    };

    fetchAndAddMaterials();

    return () => {
      cancelled = true;
    };
  }, [enabled, asin, fetchedAsins]);

  // マニュアル商品が選択されたら商品画像を取得して選択中素材に追加
  useEffect(() => {
    if (!enabled) return;
    if (!manualProductId) return;

    // 既に取得済みの商品はスキップ
    if (fetchedProductIds.has(manualProductId)) return;

    let cancelled = false;

    const fetchAndAddMaterials = async () => {
      try {
        setProductImageFetchError(null);
        setProductImageFetching(true);
        toast.show(`商品画像を取得中...`);

        // 商品取得も一時障害は自動リトライ。404 (商品が無い) 等はリトライ無駄なので即 throw。
        let product: Awaited<ReturnType<typeof getProduct>> | undefined;
        for (let attempt = 0; ; attempt++) {
          try {
            product = await getProduct(manualProductId);
            break;
          } catch (err) {
            if (cancelled) return;
            if (attempt >= PRODUCT_IMAGE_RETRY_LIMIT || !isRetriableFetchError(err)) throw err;
            toast.show(`商品の取得を再試行中... (${attempt + 1}/${PRODUCT_IMAGE_RETRY_LIMIT})`);
            await new Promise((resolve) => setTimeout(resolve, PRODUCT_IMAGE_RETRY_BASE_MS * (attempt + 1)));
            if (cancelled) return;
          }
        }

        if (cancelled) return;

        // 画像URLを収集。additional_images の {url: ''} は空き枠 (画像を外した穴) なので飛ばす
        // (空のまま fetch-url に送ると 400 になり「1枚の画像の取得に失敗」と出ていた・2026-11-05)
        const imageUrls: string[] = [];
        if (product.main_image_url?.trim()) {
          imageUrls.push(product.main_image_url.trim());
        }
        if (product.additional_images && Array.isArray(product.additional_images)) {
          for (const img of product.additional_images) {
            const raw: unknown = typeof img === 'string' ? img : (img && typeof img === 'object' && 'url' in img ? img.url : '');
            const url = typeof raw === 'string' ? raw.trim() : '';
            if (url) imageUrls.push(url);
          }
        }

        if (imageUrls.length === 0) {
          toast.error('商品に画像が登録されていません');
          setProductImageFetchError('商品に画像が登録されていません');
          return;
        }

        // 各画像を一時素材として保存
        let savedCount = 0;
        let failedCount = 0;
        for (const url of imageUrls) {
          if (cancelled) return;
          try {
            // 各画像の保存も一時障害は自動リトライ。dead URL 等は対象外で 1 回失敗扱い。
            let material: Awaited<ReturnType<typeof saveImageToTemporary>> | undefined;
            for (let attempt = 0; ; attempt++) {
              try {
                material = await saveImageToTemporary(url, undefined, {
                  product_id: manualProductId,
                  product_type: 'manual',
                });
                break;
              } catch (err) {
                if (cancelled) return;
                if (attempt >= PRODUCT_IMAGE_RETRY_LIMIT || !isRetriableFetchError(err)) throw err;
                await new Promise((resolve) => setTimeout(resolve, PRODUCT_IMAGE_RETRY_BASE_MS * (attempt + 1)));
                if (cancelled) return;
              }
            }

            if (!material) {
              // ループは break (material 代入済み) でしか抜けないため通常到達しないが、型を満たす。
              failedCount++;
              continue;
            }

            const urls = resolveMaterialUrls(material);

            // silent: true — 自動補完経路 (上記 ASIN 経路と同じ理由)
            addAsset({
              id: material.id,
              thumbnailUrl: urls.thumbnail || '',
              sourceUrl: resolveAiSourceUrl(material, urls),
              type: 'temporary',
              name: material.name ?? null,
              storageBucket: material.storage_bucket || null,
              storagePath: material.storage_path || null,
              contentType: material.content_type || null,
              usage: 'reference',
              asin: null,
              hasProductLinks: true,
              width: material.width || null,
              height: material.height || null,
              aspectRatio: calculateAspectRatio(material.width, material.height),
            }, { silent: true });
            savedCount++;
          } catch (err) {
            console.error('Failed to save image:', url, err);
            failedCount++;
          }
        }

        // 個人フォルダのキャッシュを更新
        await queryClient.refetchQueries({ queryKey: ['temporary-materials'] });

        if (savedCount > 0) {
          // 取得済み商品IDとして記録（一部でも成功した場合のみ）
          setFetchedProductIds((prev) => new Set(prev).add(manualProductId));

          if (failedCount > 0) {
            toast.success(`${savedCount}枚の画像を追加しました（${failedCount}枚は取得に失敗）`);
            setProductImageFetchError(`${failedCount}枚の画像の取得に失敗しました`);
          } else {
            toast.success(`${savedCount}枚の画像を選択中素材に追加しました`);
          }
          // 個人フォルダタブを開く
          requestOpenPersonalFolder();
        } else {
          // 全て失敗した場合はfetchedProductIdsに登録しない → リトライ可能
          toast.error(`商品画像の取得に失敗しました（${failedCount}枚）`);
          setProductImageFetchError(`商品画像の取得に失敗しました（${failedCount}枚）`);
        }
      } catch (error: any) {
        if (cancelled) return;
        console.error('Failed to fetch manual product materials:', error);
        const errorMessage = resolveErrorMessage(error);
        toast.error(`商品画像の取得に失敗しました: ${errorMessage}`);
        setProductImageFetchError(`商品画像の取得に失敗しました: ${errorMessage}`);
      } finally {
        if (!cancelled) setProductImageFetching(false);
      }
    };

    fetchAndAddMaterials();

    return () => {
      cancelled = true;
    };
  }, [enabled, manualProductId, fetchedProductIds]);

  return { productImageFetchError, setProductImageFetchError, productImageFetching };
}
